"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { LogOut, User, Settings, Search } from "lucide-react"

import { cn } from "@/lib/utils"
import { focusRing, touchTarget, utilityControl } from "@/lib/dashboard-ui"
import { resolvePageTitle } from "@/lib/dashboard-nav"
import { PLAN_RING_CLASS, getPlanLabel, getPlanTier } from "@/lib/membership"
import { authService } from "@/services/auth"
import { useAuth } from "@/hooks/use-auth"

import { ModeToggle } from "@/components/mode-toggle"
import { Button } from "@/components/ui/button"
import { MobileSidebar } from "@/components/dashboard/sidebar"
import { WalletBalance } from "@/components/layout/wallet-balance"
import { NotificationBell } from "@/components/notifications"
import { CommandPalette } from "@/components/dashboard/command-palette"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

export function DashboardHeader() {
    const { user } = useAuth()
    const pathname = usePathname()
    const [cmdOpen, setCmdOpen] = useState(false)

    const pageTitle = resolvePageTitle(pathname)
    const planLabel = getPlanLabel(user?.active_plan)
    const planRing = PLAN_RING_CLASS[getPlanTier(user?.active_plan)]

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setCmdOpen((open) => !open)
            }
        }
        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [])

    return (
        <>
            <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
            <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur-xl md:px-6">
                <MobileSidebar />

                {/* The left half of this bar used to be empty — everything was pushed
                    right with ml-auto. On a dashboard with ~40 routes, naming the
                    current page is the cheapest orientation cue there is.
                    Deliberately not an <h1>: 22 of the 46 dashboard pages already
                    render their own, and a second one in the chrome would give every
                    such page two top-level headings. */}
                <p className="hidden min-w-0 flex-1 truncate text-lg font-semibold tracking-tight text-foreground sm:block">
                    {pageTitle}
                </p>
                {/* Below sm the bar has no room for it and the page renders its own
                    heading immediately underneath, so the label would only truncate
                    to noise ("Leaderbo…"). Spacer keeps the controls right-aligned. */}
                <div className="flex-1 sm:hidden" aria-hidden="true" />

                <div className="flex items-center gap-2 md:gap-3">
                    <button
                        type="button"
                        onClick={() => setCmdOpen(true)}
                        aria-label="Search"
                        className={cn(utilityControl, touchTarget, "h-11 w-11 md:hidden")}
                    >
                        <Search className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <button
                        type="button"
                        onClick={() => setCmdOpen(true)}
                        className={cn(utilityControl, "hidden h-10 px-3 text-sm md:flex")}
                    >
                        <Search className="h-4 w-4" aria-hidden="true" />
                        <span>Search</span>
                        <kbd className="pointer-events-none ml-1 hidden select-none items-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium lg:inline-flex">
                            ⌘K
                        </kbd>
                    </button>

                    <NotificationBell size={20} className={cn(utilityControl, "h-10 w-10")} />
                    <ModeToggle className={cn(utilityControl, "h-10 w-10")} />

                    {/* The plan pill costs too much width on a phone. Below md it is
                        replaced by a ring in the same tier colour around the avatar. */}
                    <div className="hidden md:flex">
                        <WalletBalance />
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                /* The plan name rides along in the accessible name, so the
                                   mobile ring is never the only thing carrying it. */
                                aria-label={`Account menu for ${user?.full_name || "your account"} — ${planLabel} plan`}
                                className={cn(
                                    "relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-border p-0",
                                    "hover:border-primary/40 hover:bg-transparent",
                                    "transition-colors duration-150",
                                    // Mobile-only plan ring; on md+ the pill above says it in words.
                                    "ring-2 ring-offset-2 ring-offset-background md:ring-0 md:ring-offset-0",
                                    planRing,
                                    focusRing
                                )}
                            >
                                {user?.avatar_url ? (
                                    <img
                                        src={user.avatar_url}
                                        alt=""
                                        width={40}
                                        height={40}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span className="flex h-full w-full items-center justify-center bg-primary/10 text-sm font-bold text-primary">
                                        {user?.full_name ? (
                                            user.full_name.charAt(0).toUpperCase()
                                        ) : (
                                            <User className="h-4 w-4" aria-hidden="true" />
                                        )}
                                    </span>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="end" forceMount>
                            <DropdownMenuLabel className="font-normal">
                                <span className="flex flex-col gap-1">
                                    <span className="text-sm font-medium leading-none">{user?.full_name || "User"}</span>
                                    <span className="truncate text-xs leading-none text-muted-foreground">
                                        {user?.email || "user@example.com"}
                                    </span>
                                </span>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild className="cursor-pointer">
                                <Link href="/dashboard/profile" className="flex w-full items-center">
                                    <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                                    Profile &amp; settings
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {/* Sign out sits behind its own separator — a destructive-ish
                                action should not be adjacent to ordinary navigation. */}
                            <DropdownMenuItem
                                onClick={() => authService.logout()}
                                className="cursor-pointer text-destructive focus:text-destructive"
                            >
                                <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                                Log out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>
        </>
    )
}
